import React, { useMemo } from 'react';
import { ButtonPropsNovo } from './types';
import { IconWrapper, StyledButtonNovo } from './styles';
import { CircularProgress } from '@material-ui/core';
import { Spinner } from 'react-bootstrap';

/**
 * Componente de botão com variantes sólido, outline e ghost (estilo shadcn).
 *
 * @param {ButtonPropsNovo} props - Propriedades do botão.
 * @param {'button' | 'submit' | 'reset'} [props.type='button'] - Tipo do botão.
 * @param {'confirm' | 'cancel' | 'clear' | 'error' | 'default'} [props.variant='default'] - Variante de cor.
 * @param {boolean} [props.outline=false] - Estilo contornado.
 * @param {boolean} [props.ghost=false] - Estilo ghost: transparente, sem borda, hover suave.
 * @param {string} [props.width='9.375rem'] - Largura do botão.
 * @param {string} [props.height='2.5rem'] - Altura do botão.
 * @param {React.ReactNode} props.children - Conteúdo do botão.
 * @param {IconType} [props.icon] - Ícone a ser exibido.
 * @param {'left' | 'right'} [props.iconPosition='left'] - Posição do ícone.
 * @param {boolean} [props.showLoading=false] - Estado de carregamento forte.
 * @param {boolean} [props.showLoadingIcon=false] - Mostra spinner sutil antes do conteúdo.
 * @param {string | null} [props.label=null] - Texto do botão; se `null`, usa `children`.
 * @param {boolean} [props.iconOnly=false] - Botão apenas com ícone (forneça `ariaLabel`).
 * @param {string} [props.ariaLabel] - Rótulo acessível quando `iconOnly` for true.
 * @param {number} [props.iconSize=20] - Tamanho do ícone (px).
 * @returns {JSX.Element} O botão personalizado.
 */
export const CustomButtonNovo: React.FC<ButtonPropsNovo> = ({
  type = 'button',
  variant = 'default',
  width = '9.375rem',
  height = '2.5rem',
  children,
  icon,
  iconPosition = 'left',
  showLoading = false,
  label = null,
  className,
  outline = false,
  ghost = false,
  showLoadingIcon = false,
  iconOnly = false,
  ariaLabel,
  iconSize = 20,
  ...rest
}) => {
  const Icon = icon;
  const isIconOnly = !!Icon && iconOnly;

  const accessibleName = useMemo(() => {
    if (!isIconOnly) return undefined;
    return ariaLabel ?? (typeof label === 'string' ? label : undefined);
  }, [isIconOnly, ariaLabel, label]);

  if (isIconOnly && !accessibleName) {
    // eslint-disable-next-line no-console
    console.warn(
      '[CustomButtonNovo] iconOnly=true requer um ariaLabel (ou label string) para acessibilidade.',
    );
  }

  return (
    <StyledButtonNovo
      type={type}
      variant={variant}
      width={width}
      height={height}
      className={className}
      outline={outline}
      ghost={ghost}
      iconOnly={isIconOnly}
      aria-label={accessibleName}
      title={accessibleName}
      aria-busy={showLoading || showLoadingIcon ? true : undefined}
      {...rest}
    >
      {isIconOnly && (
        <IconWrapper iconOnly>
          {showLoading ? (
            <CircularProgress size={16} style={{ color: 'inherit' }} />
          ) : (
            Icon && (
              <Icon
                focusable={false}
                aria-hidden="true"
                size={iconSize}
                style={{ display: 'block' }}
                color="currentColor"
              />
            )
          )}
        </IconWrapper>
      )}

      {!isIconOnly && (
        <>
          {Icon && iconPosition === 'left' && (
            <IconWrapper position="left">
              {showLoading ? (
                <CircularProgress size={15} style={{ color: 'inherit' }} />
              ) : (
                <Icon
                  focusable={false}
                  aria-hidden="true"
                  size={iconSize}
                  style={{ display: 'block' }}
                  color="currentColor"
                />
              )}
            </IconWrapper>
          )}

          {label ?? children}

          {Icon && iconPosition === 'right' && (
            <IconWrapper position="right">
              {showLoading ? (
                <CircularProgress size={15} style={{ color: 'inherit' }} />
              ) : (
                <Icon
                  focusable={false}
                  aria-hidden="true"
                  size={iconSize}
                  style={{ display: 'block' }}
                  color="currentColor"
                />
              )}
            </IconWrapper>
          )}
        </>
      )}
    </StyledButtonNovo>
  );
};
